// src/app/modules/user/components/profile-detail/LatestVitalsCard.tsx

import React from 'react'
import {FiInbox} from 'react-icons/fi'
import moment from 'moment'
import {VerticalReading} from '../../../../shared/components'
import {VerticalDateTime} from '../../../../shared/components'
import {VerticalDivider} from '../../../../shared/components'

interface VitalsData {
  systolic: number | null
  diastolic: number | null
  pulse: number | null
  recordedAt: string
}

interface AverageVitals {
  systolic: number | null
  diastolic: number | null
  pulse: number | null
}

interface LatestVitalsCardProps {
  data: VitalsData[]
  patientId: string
  avgVitals: AverageVitals | null
  title?: string
}

const LatestVitalsCard: React.FC<LatestVitalsCardProps> = ({
  data,
  avgVitals,
  title = 'Vitals Summary',
}) => {
  const sorted = [...data].sort(
    (a, b) => moment(b.recordedAt).valueOf() - moment(a.recordedAt).valueOf()
  )
  const latest = sorted[0] || {
    systolic: null,
    diastolic: null,
    pulse: null,
    recordedAt: '',
  }

  const avgBP = {
    systolic: avgVitals?.systolic != null ? avgVitals.systolic.toFixed(0) : '--',
    diastolic: avgVitals?.diastolic != null ? avgVitals.diastolic.toFixed(0) : '--',
  }
  const avgPulse = avgVitals?.pulse != null ? avgVitals.pulse.toFixed(0) : '--'
  // before your return()
  const hasAvg =
    avgVitals !== null &&
    (avgVitals.systolic !== null || avgVitals.diastolic !== null || avgVitals.pulse !== null)

  return (
    <div className='bg-white p-3 p-md-4 rounded-4 border'>
      <div className='d-flex align-items-center gap-2'>
        <h3 className='mb-3'>{title}</h3>
        <span className='text-muted'>{`Last 30 days`}</span>
      </div>

      <div className='row row-cols-1 row-cols-md-2'>
        {/* Average Vitals */}
        <div className='col'>
          <div className='custom-card-outlined_1 p-3'>
            <div className='d-flex flex-wrap justify-content-between align-items-center mb-2 mb-md-3'>
              <h5 className='mb-0 fw-semibold'>Average Vitals</h5>
            </div>

            {hasAvg ? (
              <div className='d-flex align-items-center'>
                <VerticalReading
                  topValue={avgBP.systolic}
                  bottomValue={avgBP.diastolic}
                  bpmValue={avgPulse?.toString()}
                />
              </div>
            ) : (
              <div className='d-flex flex-column align-items-center justify-content-center text-center text-muted'>
                <FiInbox size={32} />
                <p className='mb-0'>No average vitals available</p>
              </div>
            )}
          </div>
        </div>

        {/* Last Reading */}
        <div className='col'>
          <div className='custom-card-outlined_1 p-3'>
            <div className='d-flex flex-wrap justify-content-between align-items-center mb-2 mb-md-3'>
              <h5 className='mb-0 fw-semibold'>Last Reading</h5>
            </div>

            {latest.recordedAt ? (
              <div className='d-flex align-items-center justify-content-between'>
                <VerticalReading
                  topValue={latest.systolic?.toString() || ''}
                  bottomValue={latest.diastolic?.toString() || ''}
                  bpmValue={latest.pulse?.toString()}
                />
                <VerticalDivider />
                <VerticalDateTime datetime={latest.recordedAt || ''} />
              </div>
            ) : (
              <div className='d-flex flex-column align-items-center justify-content-center text-center text-muted'>
                <FiInbox size={32} />
                <p className='mb-0'>No readings in 30 days</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default LatestVitalsCard
